import { CommonModule } from '@angular/common';
import { Component, Inject, InjectionToken, Input, OnInit, Optional, Type } from '@angular/core';
import {
  AlertController,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { DisplayService, ValidationUtil } from '@qid/core';
import { SuperButtonComponent } from '@qid/super';
import { isCancellableAutoRenewMandate } from '../../../order/interface/auto-renew-mandate.interface';
import { OrderMasterApiService } from '../../../order/services/order-master-api.service';
import { Organisation } from '../../../organisation';
import { PlanPurchase } from '../../models/plan-purchase.model';
import { PlanPurchaseMasterService } from '../../services/plan-purchase-master.service';

export const PLAN_PURCHASE_ORDER_VIEW = new InjectionToken<Type<unknown>>('PLAN_PURCHASE_ORDER_VIEW');

type PurchaseStatus = 'Active' | 'Expired' | 'Deactivated';

@Component({
  selector: 'app-view-plan-purchase-detail',
  templateUrl: './view-plan-purchase-detail.page.html',
  styleUrls: ['./view-plan-purchase-detail.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SuperButtonComponent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon,
  ],
})
export class ViewPlanPurchaseDetailPage implements OnInit {
  @Input({ required: true }) purchase!: PlanPurchase;

  private didChange = false;
  private cancellableSubscriptionIds = new Set<string>();

  constructor(
    private planPurchaseMasterService: PlanPurchaseMasterService,
    private orderMasterApiService: OrderMasterApiService,
    private alertController: AlertController,
    private modalController: ModalController,
    private displayService: DisplayService,
    @Optional() @Inject(PLAN_PURCHASE_ORDER_VIEW) private orderView: Type<unknown> | null,
  ) {}

  ngOnInit() {
    this.loadAutoRenew();
  }

  get orgName(): string {
    return this.purchase?.organisation?.name || '—';
  }

  get orgId(): string | null {
    const org = this.purchase?.organisation as Organisation | string | undefined;
    if (!org) {
      return null;
    }
    return typeof org === 'string' ? org : org._id || null;
  }

  get status(): PurchaseStatus {
    const expireTime = new Date(this.purchase.expireTime).getTime();
    if (expireTime > Date.now()) {
      return 'Active';
    }
    if (!ValidationUtil.isStringEmpty(this.purchase.deactivateRemark)) {
      return 'Deactivated';
    }
    return 'Expired';
  }

  get statusClass(): string {
    switch (this.status) {
      case 'Active':
        return 'badge-success';
      case 'Deactivated':
        return 'badge-failed';
      default:
        return 'badge-muted';
    }
  }

  get canViewOrder(): boolean {
    return !!this.purchase?.orderId && !!this.orderView;
  }

  canCancelSubscription(): boolean {
    const subscriptionId = this.purchase?.razorpaySubscriptionId;
    if (!subscriptionId || !this.purchase.autoRenews) {
      return false;
    }
    if (this.cancellableSubscriptionIds.size === 0) {
      return true;
    }
    return this.cancellableSubscriptionIds.has(subscriptionId);
  }

  close = async () => {
    await this.modalController.dismiss({ refresh: this.didChange });
  };

  openOrganisation = async () => {
    const orgId = this.orgId;
    if (!orgId) {
      return;
    }
    await this.modalController.dismiss({
      refresh: this.didChange,
      openOrganisationId: orgId,
    });
  };

  openOrder = async () => {
    const orderId = this.purchase?.orderId;
    if (!orderId || !this.orderView) {
      return;
    }
    const modal = await this.modalController.create({
      component: this.orderView as never,
      componentProps: {
        orderId,
        customerName: this.orgName !== '—' ? this.orgName : null,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.refresh) {
      this.didChange = true;
    }
    if (data?.openOrganisationId) {
      await this.modalController.dismiss({
        refresh: this.didChange,
        openOrganisationId: data.openOrganisationId,
      });
    }
  };

  copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      await this.displayService.toast({ message: 'Copied', color: 'success' });
    } catch {
      await this.displayService.toast({ message: 'Copy failed', color: 'warning' });
    }
  };

  updateExpiry = async () => {
    const alert = await this.alertController.create({
      header: 'Update expiry',
      inputs: [
        {
          type: 'date',
          name: 'expireTime',
          value: this.toDateInputValue(this.purchase.expireTime),
        },
      ],
      buttons: [
        { text: 'Back', role: 'cancel' },
        {
          text: 'Save',
          handler: (data) => {
            if (!data?.expireTime) {
              return false;
            }
            void this.saveExpiry(data.expireTime);
          },
        },
      ],
    });
    await alert.present();
  };

  cancelSubscription = async () => {
    const subscriptionId = this.purchase.razorpaySubscriptionId;
    if (!subscriptionId || !this.canCancelSubscription()) {
      return;
    }

    const ok = await this.displayService.confirm(
      'Cancel subscription?',
      'Auto-renewal will stop. The plan stays active until its expiry date.',
      'Cancel subscription',
    );
    if (!ok) {
      return;
    }

    try {
      await this.orderMasterApiService.cancelAutoRenew(subscriptionId);
      this.cancellableSubscriptionIds.delete(subscriptionId);
      this.patchPurchase({ autoRenews: false });
      this.didChange = true;
      await this.displayService.toast({
        message: `Auto-renewal cancelled. Plan remains active until ${new Date(this.purchase.expireTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.`,
        duration: 4000,
        color: 'success',
      });
    } catch {
      await this.displayService.toast({ message: 'Could not cancel subscription', color: 'danger' });
    }
  };

  removePlan = async () => {
    const alert = await this.alertController.create({
      header: 'Remove plan',
      subHeader: 'The plan will be removed immediately for this organisation.',
      inputs: [
        {
          type: 'textarea',
          label: 'Remark',
          name: 'remark',
          placeholder: 'Please mention the reason for removal',
        },
      ],
      buttons: [
        { text: 'Back', role: 'cancel' },
        {
          text: 'Remove plan',
          handler: (data) => {
            if (ValidationUtil.isStringEmpty(data.remark)) {
              return false;
            }
            void this.removeAndRefresh(data.remark);
          },
        },
      ],
    });
    await alert.present();
  };

  private async loadAutoRenew() {
    const orgId = this.orgId;
    if (!orgId) {
      return;
    }
    try {
      const mandates = await this.orderMasterApiService.readAutoRenew(orgId);
      this.cancellableSubscriptionIds = new Set(
        (mandates || [])
          .filter(isCancellableAutoRenewMandate)
          .map((mandate) => mandate.razorpaySubscriptionId),
      );
    } catch {
      this.cancellableSubscriptionIds = new Set();
    }
  }

  private async saveExpiry(dateValue: string) {
    const previousExpireTime = this.purchase.expireTime;
    const expireTime = new Date(this.toExpireTimePayload(dateValue));
    this.patchPurchase({ expireTime });
    try {
      const updated = await this.planPurchaseMasterService.updateExpiry(
        this.purchase._id,
        expireTime.toISOString(),
      );
      this.patchPurchase({ expireTime: new Date(updated?.expireTime ?? expireTime) });
      this.didChange = true;
      await this.displayService.toast({ message: 'Expiry updated', color: 'success' });
    } catch {
      this.patchPurchase({ expireTime: previousExpireTime });
      await this.displayService.toast({ message: 'Could not update expiry', color: 'danger' });
    }
  }

  private async removeAndRefresh(remark: string) {
    const previousExpireTime = this.purchase.expireTime;
    const previousRemark = this.purchase.deactivateRemark;
    this.patchPurchase({
      deactivateRemark: remark,
      expireTime: new Date(Date.now() - 1),
    });
    try {
      await this.planPurchaseMasterService.removePlan(this.purchase._id, remark);
      this.didChange = true;
      await this.displayService.toast({ message: 'Plan removed', color: 'success' });
    } catch {
      this.patchPurchase({
        deactivateRemark: previousRemark,
        expireTime: previousExpireTime,
      });
      await this.displayService.toast({ message: 'Could not remove plan', color: 'danger' });
    }
  }

  private patchPurchase(patch: Partial<PlanPurchase>) {
    this.purchase = { ...this.purchase, ...patch };
  }

  private toDateInputValue(expireTime: Date | string): string {
    const date = new Date(expireTime);
    const valid = Number.isNaN(date.getTime()) ? new Date() : date;
    const year = valid.getFullYear();
    const month = String(valid.getMonth() + 1).padStart(2, '0');
    const day = String(valid.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toExpireTimePayload(dateValue: string): string {
    const [year, month, day] = String(dateValue).slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day, 23, 59, 59, 0).toISOString();
  }
}
